// The homepage shelf: ten books Oscar keeps coming back to, in his order, each
// with one key takeaway. The thesis and its explanation are separate strings
// so the card can set them apart without markup in the data; the wording is
// his (docs/HOMEPAGE_IMPLEMENTATION_PLAN_2026-10-10.md).
//
// Covers and spines are photographs of the real books, 576 px tall
// (assets/images/homepage/manifest.json). `coverRatio` and `spineRatio` are
// width / height of the source artwork, so every face keeps its proportions
// at any shelf height.

import tisCover from "../assets/images/homepage/books/thinking-in-systems-cover.webp";
import tisSpine from "../assets/images/homepage/books/thinking-in-systems-spine.webp";
import gsbsCover from "../assets/images/homepage/books/good-strategy-bad-strategy-cover.webp";
import gsbsSpine from "../assets/images/homepage/books/good-strategy-bad-strategy-spine.webp";
import sotaCover from "../assets/images/homepage/books/sciences-of-the-artificial-cover.webp";
import sotaSpine from "../assets/images/homepage/books/sciences-of-the-artificial-spine.webp";
import aogdCover from "../assets/images/homepage/books/art-of-game-design-cover.webp";
import aogdSpine from "../assets/images/homepage/books/art-of-game-design-spine.webp";
import laCover from "../assets/images/homepage/books/lean-analytics-cover.webp";
import laSpine from "../assets/images/homepage/books/lean-analytics-spine.webp";
import tfsCover from "../assets/images/homepage/books/thinking-fast-and-slow-cover.webp";
import tfsSpine from "../assets/images/homepage/books/thinking-fast-and-slow-spine.webp";
import lofCover from "../assets/images/homepage/books/logic-of-failure-cover.webp";
import lofSpine from "../assets/images/homepage/books/logic-of-failure-spine.webp";
import lsCover from "../assets/images/homepage/books/lean-startup-cover.webp";
import lsSpine from "../assets/images/homepage/books/lean-startup-spine.webp";
import hthciCover from "../assets/images/homepage/books/how-to-have-creative-ideas-cover.webp";
import hthciSpine from "../assets/images/homepage/books/how-to-have-creative-ideas-spine.webp";
import hgCover from "../assets/images/homepage/books/hacking-growth-cover.webp";
import hgSpine from "../assets/images/homepage/books/hacking-growth-spine.webp";

/**
 * @typedef {Object} Book
 * @property {string} id            // stable slug, the selection key
 * @property {string} title
 * @property {string} author
 * @property {string} takeaway      // the thesis, set emphasized
 * @property {string} explanation
 * @property {string} coverSrc
 * @property {string} spineSrc
 * @property {number} coverRatio    // cover width / height
 * @property {number} spineRatio    // spine width / height
 */

export const INITIAL_BOOK = "thinking-in-systems";

/** @type {{sample: boolean, books: Book[]}} */
export const bookshelf = {
  sample: false,
  books: [
    {
      id: "thinking-in-systems",
      title: "Thinking in Systems",
      author: "Donella H. Meadows",
      takeaway: "Don’t just solve problems. Understand what creates them.",
      explanation:
        "Systems shape behavior. Understanding their structure, feedback loops, and incentives helps identify root causes, anticipate unintended consequences, and find high-impact interventions.",
      coverSrc: tisCover,
      spineSrc: tisSpine,
      coverRatio: 1713 / 2575,
      spineRatio: 203 / 2575,
    },
    {
      id: "good-strategy-bad-strategy",
      title: "Good Strategy, Bad Strategy",
      author: "Richard Rumelt",
      takeaway: "Strategy is about making choices, not setting goals.",
      explanation:
        "Good strategy starts with an honest diagnosis of the challenge, defines a guiding approach, and translates it into coherent actions. Focus means deciding what not to pursue.",
      coverSrc: gsbsCover,
      spineSrc: gsbsSpine,
      coverRatio: 1875 / 2850,
      spineRatio: 303 / 2850,
    },
    {
      id: "sciences-of-the-artificial",
      title: "The Sciences of the Artificial",
      author: "Herbert A. Simon",
      takeaway: "Design is about turning existing conditions into preferred ones.",
      explanation:
        "Design is a decision-making process under constraints and uncertainty. Rather than searching for perfect solutions, explore alternatives, understand trade-offs, and find solutions that are good enough to achieve the intended outcome.",
      coverSrc: sotaCover,
      spineSrc: sotaSpine,
      coverRatio: 1536 / 2314,
      spineRatio: 213 / 2314,
    },
    {
      id: "art-of-game-design",
      title: "The Art of Game Design",
      author: "Jesse Schell",
      takeaway: "Design the experience, not just the interface.",
      explanation:
        "Every design decision shapes how people think, feel, and behave. Looking at an experience through different lenses reveals hidden opportunities, challenges assumptions, and helps create more meaningful interactions.",
      coverSrc: aogdCover,
      spineSrc: aogdSpine,
      coverRatio: 1600 / 1982,
      spineRatio: 150 / 1982,
    },
    {
      id: "lean-analytics",
      title: "Lean Analytics",
      author: "Alistair Croll & Benjamin Yoskovitz",
      takeaway: "Measure what matters, not everything you can.",
      explanation:
        "The right metric depends on the product’s stage and its biggest uncertainty. Focus on one metric that matters, using actionable data to evaluate progress, challenge assumptions, and guide decisions instead of chasing vanity metrics.",
      coverSrc: laCover,
      spineSrc: laSpine,
      coverRatio: 1000 / 1500,
      spineRatio: 184 / 1500,
    },
    {
      id: "thinking-fast-and-slow",
      title: "Thinking, Fast and Slow",
      author: "Daniel Kahneman",
      takeaway: "People don’t always think rationally, and neither do designers.",
      explanation:
        "Our decisions are shaped by cognitive shortcuts, biases, and context. Understanding these mechanisms helps design for real human behavior while questioning our own assumptions and judgments.",
      coverSrc: tfsCover,
      spineSrc: tfsSpine,
      coverRatio: 1400 / 2096,
      spineRatio: 239 / 2096,
    },
    {
      id: "logic-of-failure",
      title: "The Logic of Failure",
      author: "Dietrich Dörner",
      takeaway: "Good intentions don’t guarantee good outcomes.",
      explanation:
        "Complex environments are difficult to predict and control. Acting without understanding dependencies, delays, and side effects can make problems worse. Test assumptions, monitor outcomes, and adapt decisions as new information emerges.",
      coverSrc: lofCover,
      spineSrc: lofSpine,
      coverRatio: 436 / 676,
      spineRatio: 76 / 676,
    },
    {
      id: "lean-startup",
      title: "The Lean Startup",
      author: "Eric Ries",
      takeaway: "The goal isn’t to build faster. It’s to learn faster.",
      explanation:
        "Treat product ideas as hypotheses. Build the smallest experiment that can test an assumption, measure real behavior, and use validated learning to decide whether to persevere or pivot.",
      coverSrc: lsCover,
      spineSrc: lsSpine,
      coverRatio: 1688 / 2550,
      spineRatio: 294 / 2550,
    },
    {
      id: "how-to-have-creative-ideas",
      title: "How to Have Creative Ideas",
      author: "Edward de Bono",
      takeaway: "Creativity isn’t just inspiration. It’s a skill you can practice.",
      explanation:
        "Deliberately breaking familiar thinking patterns creates new possibilities. Using random inputs, provocations, and lateral connections helps move beyond obvious solutions and explore ideas that conventional reasoning might overlook.",
      coverSrc: hthciCover,
      spineSrc: hthciSpine,
      coverRatio: 312 / 500,
      spineRatio: 58 / 500,
    },
    {
      id: "hacking-growth",
      title: "Hacking Growth",
      author: "Sean Ellis & Morgan Brown",
      takeaway: "Growth isn’t a lucky breakthrough. It’s a repeatable process.",
      explanation:
        "Sustainable growth comes from cross-functional teams continuously identifying opportunities, prioritizing experiments, testing hypotheses, and learning from results. Small improvements across acquisition, activation, retention, and revenue can compound over time.",
      coverSrc: hgCover,
      spineSrc: hgSpine,
      coverRatio: 1838 / 2775,
      spineRatio: 310 / 2775,
    },
  ],
};
